import Badge from '@components/v2/ui/Badge/Badge';
import Button from '@components/v2/ui/Button/Button';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Typography from '@components/v2/ui/Typography/Typography';
import { Grid } from '@mui/material';
import { File, Star } from 'lucide-react';
import { useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useWidth from '../../../../components/Hooks/useWidth';
import LangEnum from '../../../../lang/LangEnum';
import useGeneralSettingsStore from '../../../../stores/GeneralSettings/useGeneralSettingsStore';
import useOrderBookStore from '../../../../stores/OrderBook/orderBook.store';
import { ActiveOrderTestIds } from '../../../../utils/testIds/ActiveOrder.testIds';
import { OrderBookBadgeValueType } from '../../../general-settings/enums/orderBadgeDisplayType.enum';
import { useTranslation } from '../../../translation/TranslationContext';
import useOrderBook from '../../hooks/useOrderBook';
import { getOrderCardSummary } from '../../utils/formatters';
import OrderActiveMenuContentWithPopover from '../OrderActiveMenuContent/OrderActiveMenuContentWithPopover';
import useOrderActiveMenuStyles from './OrderActiveMenu.styles';

interface OrderActiveMenuProps {
  verifyBrandContext: (callback: () => void) => void;
}

const OrderActiveMenu = ({ verifyBrandContext }: OrderActiveMenuProps) => {
  const { openOrderBook } = useOrderBook();

  const { orderProductsIds, selectedOrder, selectedOrderSummary } = useOrderBookStore(
    useShallow((state) => ({
      orderProductsIds: state.orderProductsIds,
      selectedOrder: state.selectedOrder,
      selectedOrderSummary: state.selectedOrderSummary,
    })),
  );
  const { orderBookBadgeValueType } = useGeneralSettingsStore((state) => state.generalSettings);

  const isSuggestedOrder = selectedOrder?.status === 'SUGGESTED';

  const { classes } = useOrderActiveMenuStyles({
    isSuggestedOrder,
  });

  const [orderBookAnchorEl, setOrderBookAnchorEl] = useState<HTMLSpanElement | null>(null);
  const { mediumDownWidth } = useWidth();
  const { translate, locale } = useTranslation();
  const companyName =
    selectedOrder?.customer?.commercialName || selectedOrder?.customer?.companyName;

  const { items, pieces } = getOrderCardSummary({
    isSelectedOrder: true,
    selectedOrderSummary,
    selectedOrder,
    orderProductsIds,
    translate,
    locale,
  });

  const getBadgeDisplay = (isMobile = false): number | string => {
    const isOrderProducts = orderBookBadgeValueType === OrderBookBadgeValueType.ORDER_PRODUCTS;
    const value = isOrderProducts ? items.value : pieces.value;

    if (!selectedOrder || value <= 0) {
      return 0;
    }

    if (isMobile) {
      if (value > 9999) {
        return '9k+';
      }

      if (value > 999) {
        return `${Math.floor(value / 1000)}k`;
      }

      return value;
    }

    if (value > 99999) {
      return '99.999+';
    }

    return isOrderProducts ? items.value : pieces.formattedValue;
  };

  const OrderbookIcon = isSuggestedOrder ? Star : File;

  return (
    <Grid container>
      <Grid sx={{ display: 'flex', alignItems: 'center' }}>
        <Badge
          ref={setOrderBookAnchorEl}
          badgeContent={getBadgeDisplay(mediumDownWidth)}
          badgeTestId={ActiveOrderTestIds.ORDER_BOOK_BADGE}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{ '& .MuiBadge-badge': { transform: 'translate(0%, -50%)' } }}
          color="accent"
        >
          {mediumDownWidth ? (
            <IconButton
              ariaLabel={translate(LangEnum.ORDER_BOOK_TITLE)}
              className={classes.button}
              color="secondary"
              icon={OrderbookIcon}
              size="medium"
              isActive={!!selectedOrder}
              onClick={() => verifyBrandContext(openOrderBook)}
            />
          ) : (
            <Button
              testId={ActiveOrderTestIds.ACTIVE_ORDER_HEADER_ICON}
              color="secondary"
              variant="contained"
              size="medium"
              startIcon={<OrderbookIcon />}
              className={classes.button}
              shape="square"
              isActive={!!selectedOrder}
              onClick={() => verifyBrandContext(openOrderBook)}
            >
              <Typography noWrap component="span" variant="inherit" sx={{ maxWidth: '30ch' }}>
                {selectedOrder
                  ? companyName || translate(LangEnum.ORDER_BOOK_ORDER_WITHOUT_CUSTOMER)
                  : translate(LangEnum.ORDER_BOOK_TITLE)}
              </Typography>
            </Button>
          )}
        </Badge>

        <OrderActiveMenuContentWithPopover orderBookAnchorEl={orderBookAnchorEl} />
      </Grid>
    </Grid>
  );
};

export default OrderActiveMenu;
